import * as maplibregl from "maplibre-gl";
import { CountryAssessments } from "@app/features/other/genericApiSlice";
import {
  ACTIVE_COLOR,
  COUNTRIES_SOURCE,
  HIT_LAYER,
  HOVER_COLOR,
  INACTIVE_COLOR,
  LABELS_LAYER,
  MARKER_RADIUS_AT_MAX_ZOOM,
  MARKER_RADIUS_AT_MIN_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
  VISUAL_LAYER,
} from "../constants";
import { buildFeatures } from "./buildCountryFeatures";

export const circleRadiusExpression = [
  "interpolate",
  ["linear"],
  ["zoom"],
  MIN_ZOOM,
  MARKER_RADIUS_AT_MIN_ZOOM,
  MAX_ZOOM,
  MARKER_RADIUS_AT_MAX_ZOOM,
] as maplibregl.ExpressionSpecification;

export const hitRadiusExpression = [
  "max",
  24,
  [
    "interpolate",
    ["linear"],
    ["zoom"],
    MIN_ZOOM,
    MARKER_RADIUS_AT_MIN_ZOOM + 8,
    MAX_ZOOM,
    MARKER_RADIUS_AT_MAX_ZOOM + 8,
  ],
] as maplibregl.ExpressionSpecification;

export const getCircleColorExpression = (): maplibregl.ExpressionSpecification => [
  "case",
  ["boolean", ["feature-state", "selected"], false],
  HOVER_COLOR,
  ["==", ["get", "hasAssessments"], true],
  ACTIVE_COLOR,
  INACTIVE_COLOR,
];

export const clearSelection = (map: maplibregl.Map) => {
  if (!map.getLayer(VISUAL_LAYER)) return;
  map.removeFeatureState({ source: COUNTRIES_SOURCE });
  map.setPaintProperty(VISUAL_LAYER, "circle-color", getCircleColorExpression());
};

export const setSelectedFeature = (map: maplibregl.Map, isoCode: string | null) => {
  if (!map.getLayer(VISUAL_LAYER)) return;
  try {
    map.removeFeatureState({ source: COUNTRIES_SOURCE });
    if (isoCode) {
      map.setFeatureState(
        { source: COUNTRIES_SOURCE, id: isoCode },
        { selected: true }
      );
    }
    map.setPaintProperty(VISUAL_LAYER, "circle-color", getCircleColorExpression());
  } catch {
    // feature-state is optional visual feedback
  }
};

export const setupGlobeMap = (map: maplibregl.Map, countries: CountryAssessments[]) => {
  map.setProjection({ type: "globe" });

  if (!map.getSource(COUNTRIES_SOURCE)) {
    map.addSource(COUNTRIES_SOURCE, {
      type: "geojson",
      promoteId: "iso_code",
      data: { type: "FeatureCollection", features: buildFeatures(countries) },
    });

    map.addLayer({
      id: VISUAL_LAYER,
      type: "circle",
      source: COUNTRIES_SOURCE,
      paint: {
        "circle-radius": circleRadiusExpression,
        "circle-color": getCircleColorExpression(),
        "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], MIN_ZOOM, 2, MAX_ZOOM, 3],
        "circle-stroke-color": "#fff",
        "circle-opacity": 0.95,
      },
    });

    map.addLayer({
      id: LABELS_LAYER,
      type: "symbol",
      source: COUNTRIES_SOURCE,
      layout: {
        "text-field": ["get", "label"],
        "text-font": ["Noto Sans Regular"],
        "text-size": ["interpolate", ["linear"], ["zoom"], MIN_ZOOM, 10, MAX_ZOOM, 16],
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-max-width": 8,
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-color": "rgba(0,0,0,0.25)",
        "text-halo-width": 1,
      },
    });

    // Above labels; transparent fill (not opacity 0) so the layer stays pickable
    map.addLayer({
      id: HIT_LAYER,
      type: "circle",
      source: COUNTRIES_SOURCE,
      paint: {
        "circle-radius": hitRadiusExpression,
        "circle-color": "rgba(0, 0, 0, 0.01)",
        "circle-stroke-width": 0,
      },
    });
  } else {
    (map.getSource(COUNTRIES_SOURCE) as maplibregl.GeoJSONSource).setData({
      type: "FeatureCollection",
      features: buildFeatures(countries),
    });
  }
};
